import { useUser } from '@folklore/auth';
import { useSocket } from '@folklore/socket';
import {
    useQuery,
    keepPreviousData as previousPlaceholder,
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import { isAfter } from 'date-fns/isAfter';
import { isPast } from 'date-fns/isPast';
import { parseISO } from 'date-fns/parseISO';
import isArray from 'lodash-es/isArray';
import { useState } from 'react';

import { useApi } from '../contexts/ApiContext';

export function useUnreadNotifications(opts = null) {
    const { disabled = false } = opts || {};
    const user = useUser();
    const { items: newNotifications = null, ...queryResult } = useNewNotifications(null, {
        ...opts,
        enabled: !disabled && user !== null,
    });

    const { items: socketNotifications = null } = useSocketNotifications({
        ...opts,
        disabled,
    });

    const [notificationsRead, setNotificationsRead] = useState<string[]>([]);

    const finalItems =
        newNotifications !== null
            ? [...(newNotifications || []), ...(socketNotifications || [])]
                  .filter(({ id }) => notificationsRead.indexOf(id) === -1)
                  .reduce((acc, notification) => {
                      const index = acc.findIndex(({ id }) => id === notification.id);
                      if (index === -1) {
                          return [...acc, notification];
                      }
                      const { date } = acc[index];
                      const isNewer = isAfter(parseISO(notification.date), parseISO(date));
                      return isNewer
                          ? [...acc.slice(0, index), notification, ...acc.slice(index + 1)]
                          : acc;
                  }, [] as Notification[])
            : null;

    const { mutate: mutateMarkAsRead } = useMarkAsReadNotification();

    const markAsRead = (notificationId: string) => {
        setNotificationsRead((currentRead) => [...currentRead, notificationId]);
        mutateMarkAsRead(notificationId);
    };

    return {
        ...queryResult,
        items: finalItems,
        markAsRead,
    };
}

export function useSocketNotifications(opts = null) {
    const { disabled = false } = opts || {};
    const { id: userId = null } = useUser() || {};
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const channels = userId !== null && !disabled ? [`users.${userId}`] : [];
    const onSocketMessage = (message) => {
        const { event } = message || {};
        if (event === 'notification') {
            const { data: notification } = message || {};
            const { id: notificationId = null, readAt = null } = notification || {};
            const isRead = readAt !== null && isPast(parseISO(readAt));
            setNotifications((currentNotifications) =>
                !isRead
                    ? [...currentNotifications.filter((id) => id !== notificationId), notification]
                    : currentNotifications.filter((id) => id !== notificationId),
            );
        }
    };
    const { socket, started } = useSocket(channels, {
        onMessage: onSocketMessage,
    });

    return {
        items: notifications,
        socket,
        started,
    };
}

export function useMarkAsReadNotification(opts = {}) {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation<Notification, Error, string>({
        mutationFn: (id) => api.notifications.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications', 'new'] });
        },
        ...opts,
    });
}

export function useNewNotifications(query = null, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { data = null, ...queryResult } = useQuery<Notification[]>({
        queryKey: ['notifications', 'new', query],
        queryFn: ({ queryKey: [, , queryParam], signal }) =>
            api.notifications.new(queryParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    // const { data: items, pagination } = data || {};

    return {
        items: data,
        //   pagination,
        ...queryResult,
    };
}

type NotificationsResponse =
    | Notification[]
    | {
          data: Notification[];
          pagination: Pagination;
      };

export function useNotifications(query = null, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { data = null, ...queryResult } = useQuery<NotificationsResponse>({
        queryKey: ['notifications', query],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.notifications.get(queryParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : { items: null, ...queryResult };
}

export function useNotificationsInfinite(query = null, opts = null) {
    const api = useApi();
    const { data, isLoading, ...queryResult } = useInfiniteQuery({
        queryKey: ['notifications', 'infinite', query],
        initialPageParam: 1,
        queryFn: ({ pageParam = 1, queryKey: [, , , queryParam], signal }) =>
            api.notifications.get(queryParam, pageParam, null, { signal }),
        getNextPageParam: ({ pagination: { page: currentPage = 1, last_page: lastPage = -1 } }) => {
            if (lastPage === currentPage) {
                return undefined;
            }
            return currentPage + 1;
        },
        suspense: false,
        ...opts,
    });

    const { pages = null, pageParams = null } = data || {};

    const items =
        pages !== null
            ? (pages || []).reduce((acc, page) => [...acc, ...(page.data || [])], [])
            : null;

    const total = (pages || []).reduce(
        (currentTotal, { pagination: { total: newTotal = 0 } }) =>
            currentTotal === null || newTotal > currentTotal ? newTotal : currentTotal,
        null,
    );

    return {
        pages,
        pageParams,
        items,
        total,
        endIsReached: !isLoading && total !== null && items !== null && total <= items.length,
        isLoading,
        ...queryResult,
    };
}
